// Web Speech API wrapper — speechSynthesis is native, free, needs no
// backend. Voice coverage depends on the OS: bg-BG is decent on
// Windows / Android, patchy on Apple platforms. hasVoice(lang) lets
// the UI hide the 🔊 button when the current platform has no voice
// for the card's language, rather than silently failing on click.

import type { Lang } from "../config/langs";

function pickVoice(
  voices: readonly SpeechSynthesisVoice[],
  lang: Lang,
): SpeechSynthesisVoice | null {
  if (!voices.length) return null;
  // Exact BCP-47 match wins.
  const exact = voices.find((v) => v.lang === lang);
  if (exact) return exact;
  // Fallback: any voice whose primary subtag matches (e.g. "bg-BG" → "bg").
  const base = lang.split("-")[0]!.toLowerCase();
  return (
    voices.find((v) => v.lang.toLowerCase().startsWith(base + "-")) ||
    voices.find((v) => v.lang.toLowerCase().startsWith(base)) ||
    null
  );
}

const synth: SpeechSynthesis | null =
  typeof window !== "undefined" && "speechSynthesis" in window
    ? window.speechSynthesis
    : null;

// Voices are loaded asynchronously on Chrome and Firefox; the first
// getVoices() call often returns []. We refresh on voiceschanged and
// sweep existing buttons in the DOM: any .tts-btn we rendered as
// `hidden` because its language had no voice at render time is
// un-hidden once a matching voice becomes available.
let voices: SpeechSynthesisVoice[] = [];
function sweepButtons(): void {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll<HTMLButtonElement>(".tts-btn[hidden]")
    .forEach((btn) => {
      const lang = btn.dataset.ttsLang;
      if (lang && pickVoice(voices, lang as Lang)) btn.hidden = false;
    });
}
if (synth) {
  const refresh = () => {
    voices = synth.getVoices();
    sweepButtons();
  };
  refresh();
  synth.addEventListener?.("voiceschanged", refresh);
}

export const tts = {
  isSupported(): boolean {
    return !!synth;
  },
  hasVoice(lang: Lang): boolean {
    return !!pickVoice(voices, lang);
  },
  speak(text: string, lang: Lang): void {
    if (!synth || !text.trim()) return;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const v = pickVoice(voices, lang);
    if (v) {
      u.voice = v;
      u.lang = v.lang;
    } else {
      u.lang = lang;
    }
    // Slightly slower than default — learner-friendly without sounding robotic.
    u.rate = 0.95;
    synth.speak(u);
  },
  cancel(): void {
    synth?.cancel();
  },
};

/** Install a single document-level click delegate. Any button carrying
 *  the `.tts-btn` class and `data-tts-text` + `data-tts-lang` attrs
 *  triggers speak() — so UI code can emit those buttons through
 *  innerHTML without rebinding listeners after every render. */
export function installTtsDelegation(): void {
  if (!synth) return;
  document.addEventListener("click", (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const btn = target.closest<HTMLButtonElement>(".tts-btn");
    if (!btn) return;
    const text = btn.dataset.ttsText;
    const lang = btn.dataset.ttsLang;
    if (text && lang) tts.speak(text, lang as Lang);
  });
}
